import * as React from 'react';
import { useSelector } from 'react-redux';
import { ArticleHeading3, ArticleParagraph2, LinkButton } from '@cian/ui-kit';
import {
  NewBuildingCard as NewBuildingCardComponent,
  NewBuildingCardListed,
} from '../../components/NewBuildingCardRedesigned';
import { Link } from '../../components/Link';
import { IApplicationState } from '../../types/redux';
import { getDeveloperId, getFilterRegionId, getNewBuildingById, getNewbuildingsJsonQuery } from '../../selectors';
import { TViewMode } from '../Projects/types';
import { CardImage } from '../CardImage';
import { SubwayInfo } from '../SubwayInfo';
import { ApartmentsList, ApartmentsListGroup } from '../ApartmentsList';

import { SPThemeWrapper } from '../../components/SPThemeWrapper';
import { getNewBuildingImages } from '../NewBuildingCard/helpers';
import {
  trackApartmentsCardClick,
  trackImageCardClick,
  trackMoreButtonCardClick,
  trackSeeAllButtonCardClick,
  trackTitleCardClick,
} from '../NewBuildingCard/tracking';

interface INewBuildingCardProps {
  newbuildingId: number;
  viewMode: TViewMode;
  index: number;
  singleCard?: boolean;
}

export function NewBuildingCard({ newbuildingId, viewMode, index, singleCard }: INewBuildingCardProps) {
  const developerId = useSelector(getDeveloperId);
  const newbuilding = useSelector((state: IApplicationState) => getNewBuildingById(state, newbuildingId));
  const sc = useSelector(getNewbuildingsJsonQuery);
  const regionId = useSelector(getFilterRegionId);
  const images = getNewBuildingImages(newbuilding);

  const title = <ArticleHeading3>{newbuilding.title}</ArticleHeading3>;
  const finishLabel = newbuilding.finishLabel ? <ArticleParagraph2>{newbuilding.finishLabel}</ArticleParagraph2> : null;
  const address = <ArticleParagraph2 color="gray60_100">{newbuilding.address}</ArticleParagraph2>;
  const url = newbuilding.publicUrl || '';
  const subwayInfo = <SubwayInfo subwayInfo={newbuilding.subwayInfo} />;
  const trackingArguments = React.useMemo(
    () => ({ newbuilding, developerId, position: index + 1, regionId, sc: sc as Object }),
    [developerId, index, newbuilding, regionId, sc],
  );

  if (viewMode === 'table') {
    return (
      <NewBuildingCardComponent
        isReliable={newbuilding.isReliable}
        image={<CardImage src={newbuilding.image} borderRadius={0} />}
        title={title}
        url={url}
        finishLabel={finishLabel}
        subwayInfo={subwayInfo}
        address={address}
        apartments={<ApartmentsList newBuildingId={newbuilding.id} />}
        onTitleClick={trackTitleCardClick(trackingArguments)}
        onImageClick={trackImageCardClick(trackingArguments)}
        onApartmentsClick={trackApartmentsCardClick(trackingArguments)}
        reviewsStats={newbuilding.reviewsStats}
        singleCard={singleCard}
      />
    );
  }

  return (
    <NewBuildingCardListed
      isReliable={newbuilding.isReliable}
      images={images}
      title={title}
      finishLabel={finishLabel}
      subwayInfo={subwayInfo}
      address={address}
      apartments={<ApartmentsListGroup newBuildingId={newbuilding.id} />}
      detailsButton={
        <SPThemeWrapper>
          <LinkButton
            size="XS"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackMoreButtonCardClick(trackingArguments)}
          >
            Подробнее о ЖК
          </LinkButton>
        </SPThemeWrapper>
      }
      seeAll={
        newbuilding.searchList ? (
          <Link
            url={newbuilding.searchList}
            text="Смотреть все квартиры"
            onClick={trackSeeAllButtonCardClick(trackingArguments)}
          />
        ) : null
      }
    />
  );
}
